// Compress an image File/Blob client-side before uploading.
// Returns a data URL (base64 JPEG) limited to maxDim px on the long edge.
//
// Strategy (order matters — this is what prevents the "black photo" bug):
//   1. Decode the ORIGINAL file with the browser first. This is correct for JPEG/PNG
//      everywhere AND for HEIC/HEIF on Safari/iOS (WebKit decodes HEIC natively). We try
//      multiple decoders (createImageBitmap with & without EXIF orientation, then <img>)
//      and pick the first that produces a NON-BLANK canvas.
//   2. Only if every native decoder fails or yields a blank/black frame (typical on
//      Chrome / Android for HEIC) do we transcode via heic2any (libheif WASM) and retry.
//   3. A white background is painted before drawImage so images with alpha don't turn
//      black when exported to JPEG.
//   4. Every candidate is validated with a blank/near-uniform detector, so a broken
//      decode is NEVER silently stored — the user gets an actionable error instead.
import { log } from "./logger";


const HEIC_MIME_RE = /heic|heif/i;
const HEIC_NAME_RE = /\.(heic|heif)$/i;
const OK_NAME_RE = /\.(jpe?g|png|gif|webp|bmp)$/i;

async function convertHeicToJpeg(file) {
  const heic2any = (await import("heic2any")).default;
  const result = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 });
  const blob = Array.isArray(result) ? result[0] : result;
  if (!blob || !blob.size) throw new Error("empty conversion result");
  return blob;
}

// Reliable HEIC conversion on the server (libheif via pillow-heif). Used only when the
// browser can't decode HEIC natively AND the client-side converter blanks/fails — i.e.
// the HDR/10-bit HEIC on Android that produced black photos. Returns a JPEG data URL.
async function convertHeicOnServer(file, maxDim, quality) {
  const { api } = await import("./api");
  const fd = new FormData();
  fd.append("file", file, file.name || "photo.heic");
  const r = await api.post(
    `/leads/heic-to-jpeg?max_dim=${maxDim}&quality=${Math.round(quality * 100)}`,
    fd,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  const data = r?.data?.data;
  return data && data.startsWith("data:image/") ? data : null;
}

function loadImgElement(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read-failed"));
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("decode-failed"));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

// True when the drawn canvas is effectively a single flat colour (all-black, all-white,
// etc.) — i.e. the decode produced nothing usable. Real photos always have variance.
function isCanvasBlank(ctx, w, h) {
  let data;
  try {
    data = ctx.getImageData(0, 0, w, h).data;
  } catch {
    return false; // can't read (shouldn't happen for same-origin) — assume not blank
  }
  const total = w * h;
  const step = Math.max(1, Math.floor(total / 4096)); // sample ~4k pixels
  let min = 255, max = 0;
  for (let i = 0; i < total; i += step) {
    const o = i * 4;
    // perceived luminance
    const lum = (data[o] * 0.299 + data[o + 1] * 0.587 + data[o + 2] * 0.114);
    if (lum < min) min = lum;
    if (lum > max) max = lum;
    if (max - min > 4) return false; // enough variance → real content
  }
  return (max - min) <= 4;
}

// Draw a decoded source (ImageBitmap or <img>) into a resized JPEG data URL.
// Returns the data URL, or null if the source is unusable / the result is blank.
function drawToJpeg(source, maxDim, quality) {
  const srcW = source.width || source.naturalWidth || 0;
  const srcH = source.height || source.naturalHeight || 0;
  if (!srcW || !srcH) return null;

  let target = maxDim;
  for (let attempt = 0; attempt < 4; attempt++) {
    const scale = Math.min(1, target / Math.max(srcW, srcH));
    const w = Math.max(1, Math.round(srcW * scale));
    const h = Math.max(1, Math.round(srcH * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      target = Math.round(target / 2);
      if (target < 256) break;
      continue;
    }
    // White backdrop so transparent pixels don't become black in the JPEG.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    try {
      ctx.drawImage(source, 0, 0, w, h);
      if (isCanvasBlank(ctx, w, h)) return null; // decode yielded a blank/black frame
      const out = canvas.toDataURL("image/jpeg", quality);
      if (out && out.startsWith("data:image/")) return out;
    } catch (e) {
      // canvas too big / OOM on low-end device — shrink and retry
      log.warn("[image] canvas draw/export failed; shrinking and retrying", e);
    }
    target = Math.round(target / 2);
    if (target < 256) break;
  }
  return null;
}

// Try several native decoders in order; return the first non-blank JPEG, else null.
async function decodeAndCompress(file, maxDim, quality) {
  const decoders = [];
  if (typeof createImageBitmap === "function") {
    decoders.push(() => createImageBitmap(file, { imageOrientation: "from-image" }));
    decoders.push(() => createImageBitmap(file)); // Safari: orientation option can blank the frame
  }
  decoders.push(() => loadImgElement(file));

  for (const dec of decoders) {
    let source;
    try {
      source = await dec();
    } catch {
      continue; // this decoder can't handle the file — try the next
    }
    const out = drawToJpeg(source, maxDim, quality);
    if (source && typeof source.close === "function") source.close();
    if (out) return out;
  }
  return null;
}

export async function compressImage(file, { maxDim = 1600, quality = 0.75 } = {}) {
  if (!file) throw new Error("No file was selected.");

  const mime = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  const isHeic = HEIC_MIME_RE.test(mime) || HEIC_NAME_RE.test(name);

  if (!isHeic) {
    const looksLikeImage = mime.startsWith("image/") || OK_NAME_RE.test(name);
    if (!looksLikeImage) {
      throw new Error("That doesn't look like a photo. Please pick a JPG or PNG image.");
    }
  }

  // 1) Native decode of the original file (correct for HEIC on Safari/iOS + all normal images).
  let out = await decodeAndCompress(file, maxDim, quality);
  if (out) return out;

  // 2) HEIC that no native decoder could handle (Chrome / Android): try client-side
  //    transcode first, and if that blanks/fails fall back to reliable server conversion.
  if (isHeic) {
    // (a) client-side heic2any — fast, works for many HEIC files
    try {
      const jpegBlob = await convertHeicToJpeg(file);
      out = await decodeAndCompress(jpegBlob, maxDim, quality);
      if (out) return out;
    } catch (e) { log.warn("[image] client-side HEIC decode failed; trying server", e); }

    // (b) server-side libheif — handles HDR/10-bit HEIC that heic2any renders black
    try {
      out = await convertHeicOnServer(file, maxDim, quality);
      if (out) return out;
    } catch (e) { log.warn("[image] server-side HEIC conversion failed", e); }

    throw new Error(
      "This HEIC photo couldn't be processed. Please check your connection and try again, or switch your Camera to JPEG (Settings → Camera → Formats → Most Compatible) and upload again."
    );
  }

  throw new Error("Your device couldn't process this photo. Please try a different image, or restart the browser and retry.");
}
